import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Lightbulb, X } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { useFirstSeen } from '../hooks/useFirstSeen';

export type CardPlacement = 'bottom' | 'right' | 'top' | 'left';

export interface FirstSeenCardProps {
    /** Unique key for this card. Persisted to the user's ``seen`` list. */
    cardKey: string;
    /** Anchor element to position the card next to. The card hides if
     *  the ref is null (e.g., the target hasn't mounted yet). */
    targetRef: React.RefObject<HTMLElement | null>;
    /** Card title — bolded, lead with what the thing is. */
    title: string;
    /** Card body — components (so callers can include lists, strong text, etc.). */
    children: React.ReactNode;
    /** Preferred placement; auto-flips to the opposite side if off-screen. */
    placement?: CardPlacement;
    /** Pixel offset from the target. Default 10. */
    gap?: number;
}

const CARD_WIDTH = 320;
const VIEWPORT_PADDING = 12;

interface ResolvedPosition {
    top: number;
    left: number;
    placement: CardPlacement;
}

const computePosition = (
    rect: DOMRect,
    cardWidth: number,
    cardHeight: number,
    preferred: CardPlacement,
    gap: number,
): ResolvedPosition => {
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    const positionsFor = (placement: CardPlacement): ResolvedPosition => {
        switch (placement) {
            case 'bottom':
                return {
                    top: rect.bottom + gap,
                    left: rect.left + rect.width / 2 - cardWidth / 2,
                    placement,
                };
            case 'top':
                return {
                    top: rect.top - gap - cardHeight,
                    left: rect.left + rect.width / 2 - cardWidth / 2,
                    placement,
                };
            case 'right':
                return {
                    top: rect.top + rect.height / 2 - cardHeight / 2,
                    left: rect.right + gap,
                    placement,
                };
            case 'left':
                return {
                    top: rect.top + rect.height / 2 - cardHeight / 2,
                    left: rect.left - gap - cardWidth,
                    placement,
                };
        }
    };

    const flipOf: Record<CardPlacement, CardPlacement> = {
        bottom: 'top', top: 'bottom', right: 'left', left: 'right',
    };

    const fits = (pos: ResolvedPosition): boolean =>
        pos.top >= VIEWPORT_PADDING
        && pos.left >= VIEWPORT_PADDING
        && pos.top + cardHeight <= viewportH - VIEWPORT_PADDING
        && pos.left + cardWidth <= viewportW - VIEWPORT_PADDING;

    const candidates: CardPlacement[] = [preferred, flipOf[preferred]];
    for (const candidate of candidates) {
        const pos = positionsFor(candidate);
        if (fits(pos)) return pos;
    }
    // Nothing fits — clamp the preferred placement into the viewport.
    const pos = positionsFor(preferred);
    pos.left = Math.max(VIEWPORT_PADDING, Math.min(viewportW - cardWidth - VIEWPORT_PADDING, pos.left));
    pos.top = Math.max(VIEWPORT_PADDING, Math.min(viewportH - cardHeight - VIEWPORT_PADDING, pos.top));
    return pos;
};

/* ── Styled ───────────────────────────────────────────────────── */

const Card = styled.div<{ $top: number; $left: number }>`
    position: fixed;
    top: ${({ $top }) => $top}px;
    left: ${({ $left }) => $left}px;
    width: ${CARD_WIDTH}px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
    padding: 14px 16px 12px;
    /* Sits below modals (z-index 1000) so welcome / reprocess / delete
       overlays cleanly cover any first-seen card that happens to be open. */
    z-index: 900;
    font-size: 13px;
    line-height: 1.45;
    color: #1e293b;
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
`;

const HeaderIcon = styled.span`
    display: inline-flex;
    color: #f59e0b;
    flex-shrink: 0;
`;

const Title = styled.h3`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    flex: 1 1 auto;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    padding: 2px;
    margin: -2px -4px -2px 0;
    cursor: pointer;
    color: #94a3b8;
    display: inline-flex;
    border-radius: 4px;

    &:hover { color: #475569; background: #f1f5f9; }
`;

const Body = styled.div`
    color: #1e293b;
    margin-bottom: 12px;

    p {
        margin: 0 0 8px;
        &:last-child { margin-bottom: 0; }
    }

    ul {
        margin: 6px 0 0;
        padding-left: 18px;
    }

    li { margin-bottom: 4px; }

    strong { color: #0f172a; }
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SuppressLink = styled.button`
    background: none;
    border: none;
    padding: 0;
    margin-right: auto;
    color: #94a3b8;
    font-size: 11px;
    font-family: inherit;
    cursor: pointer;
    text-decoration: underline;

    &:hover { color: #64748b; }
`;

const GotItButton = styled.button`
    background: #0f172a;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;

    &:hover { background: #1e293b; }
`;

/* ── Component ────────────────────────────────────────────────── */

export const FirstSeenCard: React.FC<FirstSeenCardProps> = ({
    cardKey,
    targetRef,
    title,
    children,
    placement = 'bottom',
    gap = 10,
}) => {
    const { shouldShow, markSeen } = useFirstSeen(cardKey);
    const { setSuppressAll } = useAuth();
    const cardRef = React.useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = React.useState<ResolvedPosition | null>(null);

    // (Re)compute the card's position relative to the target. Runs on
    // mount, target ref changes, viewport resize, and scroll. The
    // measurement uses the card's actual rendered size so the
    // viewport-fit check sees real dimensions.
    React.useLayoutEffect(() => {
        if (!shouldShow) return;
        const target = targetRef.current;
        const card = cardRef.current;
        if (!target || !card) return;

        const compute = () => {
            const targetRect = target.getBoundingClientRect();
            const cardRect = card.getBoundingClientRect();
            setPosition(computePosition(targetRect, cardRect.width, cardRect.height, placement, gap));
        };

        compute();

        const ro = new ResizeObserver(compute);
        ro.observe(target);
        ro.observe(card);
        window.addEventListener('scroll', compute, true);
        window.addEventListener('resize', compute);
        return () => {
            ro.disconnect();
            window.removeEventListener('scroll', compute, true);
            window.removeEventListener('resize', compute);
        };
    }, [shouldShow, targetRef, placement, gap]);

    if (!shouldShow) return null;

    // Render the card off-screen on the first paint until layout settles
    // — avoids a flash at the top-left while `position` is still null.
    const top = position?.top ?? -10000;
    const left = position?.left ?? -10000;

    return createPortal(
        <Card ref={cardRef} role="dialog" aria-labelledby={`first-seen-${cardKey}`} $top={top} $left={left}>
            <CardHeader>
                <HeaderIcon><Lightbulb size={14} /></HeaderIcon>
                <Title id={`first-seen-${cardKey}`}>{title}</Title>
                <CloseButton
                    type="button"
                    aria-label="Dismiss tip"
                    onClick={markSeen}
                >
                    <X size={14} />
                </CloseButton>
            </CardHeader>
            <Body>{children}</Body>
            <Footer>
                <SuppressLink
                    type="button"
                    onClick={() => { void setSuppressAll(true); }}
                >
                    Don&apos;t show first-time tips
                </SuppressLink>
                <GotItButton type="button" onClick={markSeen}>Got it</GotItButton>
            </Footer>
        </Card>,
        document.body,
    );
};

export default FirstSeenCard;
